'use client'

import * as Toast from '@radix-ui/react-toast'
import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import type React from 'react'
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState
} from 'react'

import { cn } from '@/helpers/utils'

type ToastType = 'success' | 'error' | 'info' | 'warn'

type ToastData = {
	title: string
	description?: string
	type?: ToastType
}

type ToastContextType = {
	toast: (data: ToastData) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
	const context = useContext(ToastContext)
	if (!context) throw new Error('useToast must be used within a ToastProvider')
	return context.toast
}

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
	const [open, setOpen] = useState(false)
	const [toastData, setToastData] = useState<ToastData | null>(null)

	const showToast = useCallback((data: ToastData) => {
		setToastData(data)
		setOpen(false)
		setTimeout(() => setOpen(true), 10)
	}, [])

	const getStyles = (type?: ToastType) => {
		switch (type) {
			case 'success':
				return {
					container: 'bg-teal-100 text-teal-800 border-teal-500',
					icon: <CheckCircle className='text-emerald-700 w-5 h-5' />
				}
			case 'error':
				return {
					container: 'bg-red-100 text-red-800 border-red-300',
					icon: <AlertCircle className='text-red-700 w-5 h-5' />
				}
			case 'info':
				return {
					container: 'bg-sky-100 text-sky-800 border-sky-300',
					icon: <Info className='text-sky-700 w-5 h-5' />
				}
			case 'warn':
				return {
					container: 'bg-yellow-100 text-yellow-800 border-yellow-300',
					icon: <AlertTriangle className='text-yellow-700 w-5 h-5' />
				}
			default:
				return {
					container: 'bg-gray-100 text-gray-800 border-gray-300',
					icon: null
				}
		}
	}

	const styles = getStyles(toastData?.type)
	const value = useMemo(() => ({ toast: showToast }), [showToast])

	return (
		<ToastContext.Provider value={value}>
			<Toast.Provider swipeDirection='right'>
				{children}
				<Toast.Root
					open={open}
					onOpenChange={setOpen}
					className={cn(
						'flex items-start gap-3 p-4 rounded-xl shadow-lg border-0 max-w-md border-t-3',
						styles.container
					)}
				>
					{styles.icon && <div className='mt-1'>{styles.icon}</div>}
					<div className='flex flex-col'>
						<Toast.Title className='font-medium'>
							{toastData?.title}
						</Toast.Title>
						{toastData?.description && (
							<Toast.Description className='text-sm'>
								{toastData.description}
							</Toast.Description>
						)}
					</div>
				</Toast.Root>
				<Toast.Viewport className='fixed top-4 right-4 z-50 w-auto max-w-sm' />
			</Toast.Provider>
		</ToastContext.Provider>
	)
}
